import React, { useState, useEffect } from "react";
import { Check, Zap, ShieldCheck, Clock, ArrowUpRight, TrendingUp, Sparkles, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiService from "../services/api";
import InvestmentModal from "./InvestmentModal";

export default function PacksSection() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await apiService.get('/investments/plans');
      if (response.success && response.data) {
        // Enrichir les plans avec les informations de mining
        const enrichedPlans = response.data.map(plan => ({
          ...plan,
          hasMining: parseFloat(plan.daily_mining_rate || 0) > 0 && plan.mining_asset
        }));
        setPlans(enrichedPlans);
      }
    } catch (error) {
      console.error("Error fetching investment plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  // Color themes for each plan
  const planThemes = [
    { accent: '#6366f1', accentRgb: '99,102,241', glow: 'indigo', gradient: 'from-indigo-500 to-blue-500' },
    { accent: '#06b6d4', accentRgb: '6,182,212', glow: 'cyan', gradient: 'from-cyan-500 to-blue-400' },
    { accent: '#8b5cf6', accentRgb: '139,92,246', glow: 'violet', gradient: 'from-violet-500 to-purple-500' },
    { accent: '#f59e0b', accentRgb: '245,158,11', glow: 'amber', gradient: 'from-amber-500 to-orange-500' },
    { accent: '#10b981', accentRgb: '16,185,129', glow: 'emerald', gradient: 'from-emerald-500 to-teal-500' },
  ];

  return (
    <section className="py-20 md:py-28 relative overflow-hidden"
             style={{ background: 'linear-gradient(180deg, #060a14 0%, #0a0f1e 50%, #060a14 100%)' }}>
      {/* Background Effects */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full blur-[180px] pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)' }}></div>
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[150px] pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.05) 0%, transparent 70%)' }}></div>
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20 animate-fade-in">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-6"
               style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}>
            <Zap size={14} className="text-indigo-400 animate-pulse" />
            <span className="font-black text-[10px] text-indigo-400 uppercase tracking-[0.25em]">{t('investments.growth_opportunities')}</span>
          </div>
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-black mb-5 md:mb-6 tracking-tight text-white font-outfit uppercase leading-none">
            {(() => {
                const title = t('investments.optimized_packs');
                const words = title.split(' ');
                if (words.length < 2) return title;
                const firstPart = words.slice(0, -1).join(' ');
                const lastWord = words[words.length - 1];
                return (
                    <>
                        {firstPart} <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">{lastWord}</span>
                    </>
                );
            })()}
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto leading-relaxed font-medium">
            {t('investments.maximize_yield')}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-6">
            <div className="relative">
              <div className="w-16 h-16 border-t-2 border-indigo-500 rounded-full animate-spin" style={{ boxShadow: '0 0 30px rgba(99,102,241,0.3)' }}></div>
            </div>
            <p className="text-slate-500 font-black uppercase tracking-[0.4em] text-[10px]">{t('investments.syncing_plans')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
            {plans.map((plan, index) => {
              const popular = index === 1;
              const theme = planThemes[index % planThemes.length];
              
              const yieldPct = parseFloat(plan.daily_return_percent);
              const days = parseInt(plan.duration_days);
              const minAmt = parseFloat(plan.min_amount);
              
              const totalYield = yieldPct * days;
              const totalGain = (minAmt * totalYield) / 100;
              const finalCap = minAmt + totalGain;

              return (
                <div key={plan.id} className="group relative">
                  {/* Hover Glow */}
                  <div className="absolute -inset-1 rounded-[28px] opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                       style={{ background: `linear-gradient(135deg, rgba(${theme.accentRgb},0.12), rgba(${theme.accentRgb},0.04))`, filter: 'blur(20px)' }}></div>
                  
                  {/* Main Card */}
                  <div className={`relative rounded-[24px] overflow-hidden transition-all duration-500 group-hover:-translate-y-1 ${popular ? 'ring-1' : ''}`}
                       style={{
                         background: `linear-gradient(180deg, rgba(12,18,35,0.95) 0%, rgba(8,12,25,0.98) 100%)`,
                         border: `1px solid rgba(${theme.accentRgb},${popular ? '0.2' : '0.06'})`,
                         boxShadow: popular ? `0 0 40px rgba(${theme.accentRgb},0.08), 0 20px 60px rgba(0,0,0,0.4)` : '0 20px 60px rgba(0,0,0,0.3)',
                         ...(popular ? { '--tw-ring-color': `rgba(${theme.accentRgb},0.3)` } : {})
                       }}>

                    {/* Popular Badge */}
                    {popular && (
                      <div className="absolute top-0 left-0 right-0 h-[2px]"
                           style={{ background: `linear-gradient(90deg, transparent, rgba(${theme.accentRgb},0.6), transparent)` }}></div>
                    )}

                    {/* Card Content */}
                    <div className="p-5 md:p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-5">
                        <div>
                          <div className="flex items-center gap-2.5 mb-2">
                            <h3 className="text-xl md:text-2xl font-black text-white font-outfit uppercase tracking-tight">{plan.name}</h3>
                            {popular && (
                              <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest"
                                    style={{ background: `rgba(${theme.accentRgb},0.15)`, color: theme.accent, border: `1px solid rgba(${theme.accentRgb},0.2)` }}>
                                <Sparkles size={8} className="inline mr-1" />
                                {t('investments.best_value')}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                            <span className="flex items-center gap-1"><Clock size={11} /> {days} {t('investments.days')}</span>
                            <span className="flex items-center gap-1"><Check size={11} className="text-emerald-500" /> {t('investments.daily_withdrawal')}</span>
                          </div>
                        </div>
                        {/* Plan Number */}
                        <div className="text-3xl font-black font-outfit opacity-10 select-none" style={{ color: theme.accent }}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                      </div>

                      {/* Yield Display */}
                      <div className="mb-5 p-4 rounded-2xl relative overflow-hidden"
                           style={{ background: `rgba(${theme.accentRgb},0.04)`, border: `1px solid rgba(${theme.accentRgb},0.08)` }}>
                        <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-[60px] pointer-events-none"
                             style={{ background: `radial-gradient(circle, rgba(${theme.accentRgb},0.1) 0%, transparent 70%)` }}></div>
                        <div className="relative z-10 flex items-end justify-between">
                          <div>
                            <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.2em] block mb-1">{t('investments.daily_yield')}</span>
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl md:text-4xl font-black font-outfit" style={{ color: theme.accent }}>
                                +{yieldPct.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
                              </span>
                              <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest">/ {t('investments.day')}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest block mb-0.5">{t('investments.from')}</span>
                            <span className="text-white font-black text-lg font-outfit">{minAmt.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[10px] text-slate-500">{plan.symbol}</span></span>
                          </div>
                        </div>
                      </div>

                      {/* Mini Chart */}
                      <div className="mb-5 relative h-14 w-full flex items-end overflow-hidden opacity-60 group-hover:opacity-100 transition-opacity rounded-xl overflow-hidden">
                        <svg className="absolute bottom-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                          <defs>
                            <linearGradient id={`grad-${plan.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor={theme.accent} stopOpacity="0.4" />
                              <stop offset="100%" stopColor={theme.accent} stopOpacity="0.1" />
                            </linearGradient>
                          </defs>
                          <path d={`M0,100 L0,75 Q30,70 60,40 Q80,25 100,10 L100,100 Z`} fill={`url(#grad-${plan.id})`} />
                          <path d={`M0,75 Q30,70 60,40 Q80,25 100,10`} stroke={theme.accent} strokeWidth="1.5" fill="none" opacity="0.5" className="group-hover:opacity-100 transition-opacity" />
                        </svg>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-3 gap-2 mb-5">
                        <div className="p-2.5 rounded-xl text-center" style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.03)' }}>
                          <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest block mb-0.5">Total</span>
                          <span className="text-xs font-black text-white font-outfit">+{totalYield.toFixed(1)}%</span>
                        </div>
                        <div className="p-2.5 rounded-xl text-center" style={{ background: 'rgba(10,30,20,0.4)', border: '1px solid rgba(16,185,129,0.06)' }}>
                          <span className="text-[7px] font-black text-emerald-500/50 uppercase tracking-widest block mb-0.5">Gain</span>
                          <span className="text-xs font-black text-emerald-400 font-outfit">+{totalGain.toFixed(1)}</span>
                        </div>
                        <div className="p-2.5 rounded-xl text-center" style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.03)' }}>
                          <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest block mb-0.5">Final</span>
                          <span className="text-xs font-black text-white font-outfit">{finalCap.toFixed(1)}</span>
                        </div>
                      </div>

                      {/* Max Amount */}
                      {plan.max_amount && (
                        <div className="flex items-center gap-1.5 mb-4 text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                          <ShieldCheck size={11} style={{ color: theme.accent }} />
                          Max {parseFloat(plan.max_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} {plan.symbol}
                        </div>
                      )}

                      {/* Mining Bonus */}
                      {plan.hasMining && (
                        <div className="flex items-center gap-1.5 mb-4 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                          <Zap size={12} className="text-emerald-400" />
                          <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider">
                            +{parseFloat(plan.daily_mining_rate).toLocaleString(undefined, { maximumFractionDigits: 2 })} {plan.mining_asset}/day
                          </span>
                        </div>
                      )}

                      {/* CTA Button */}
                      <button onClick={() => handleActivate(plan)}
                              className="w-full py-3.5 rounded-xl font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all duration-300 group/btn relative overflow-hidden"
                              style={{
                                background: `linear-gradient(135deg, rgba(${theme.accentRgb},0.15) 0%, rgba(${theme.accentRgb},0.05) 100%)`,
                                border: `1px solid rgba(${theme.accentRgb},0.2)`,
                                color: theme.accent
                              }}>
                        <span className="relative z-10 flex items-center gap-2">
                          {t('investments.activate_pack')} <ArrowUpRight size={14} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                        </span>
                        <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"
                             style={{ background: `linear-gradient(135deg, rgba(${theme.accentRgb},0.1) 0%, rgba(${theme.accentRgb},0.02) 100%)` }}></div>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Security Banner */}
        <div className="mt-12 md:mt-20 p-6 md:p-8 rounded-[24px] relative overflow-hidden max-w-4xl mx-auto"
             style={{
               background: 'linear-gradient(135deg, rgba(12,18,35,0.9) 0%, rgba(10,15,30,0.95) 100%)',
               border: '1px solid rgba(99,102,241,0.1)',
               boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
             }}>
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}>
                <Layers size={24} className="text-indigo-400" />
              </div>
              <div>
                <h4 className="text-lg md:text-xl font-black text-white font-outfit uppercase tracking-tight">{t('investments.security_guarantee')}</h4>
                <p className="text-slate-400 font-medium text-sm leading-relaxed">{t('investments.security_desc')}</p>
              </div>
            </div>
            <Link 
              to="/dashboard"
              className="flex-shrink-0 flex items-center gap-2 px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all duration-300"
              style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
            >
              {t('investments.back_to_terminal')}
            </Link>
          </div>
        </div>
      </div>

      <InvestmentModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        plan={selectedPlan}
        onStatusChange={fetchPlans}
      />
    </section>
  );
}